import React from 'react';
import styles from './Pagination.module.scss';

import Select from 'react-select';
import APPCONSTANTS from '../../constants/appConstants';
import PaginationInput from './PaginationInput';

const Anchor = ({ content, onClick, className }: any) => (
  <li role='presentation' className={className} onClick={onClick}>
    <div className={styles.paginationButton}>{content}</div>
  </li>
);

interface IPaginationProps {
  length: number;
  total: number;
  onChangePage: (page: number, rowsPerPage: number) => void;
  initialPage: number;
  currentPage: number;
  onChangeRowsPerPage: (rowsPerPage: number) => void;
}

interface IPagintaionState {
  pages: Array<number | string>;
  totalPages: number;
  currentPage: number;
}

class Pagination extends React.Component<IPaginationProps, IPagintaionState> {
  static getDerivedStateFromProps(nextProps: IPaginationProps, prevState: IPagintaionState) {
    const { currentPage, total, length } = nextProps;
    const totalPages = Math.ceil((total || 0) / length);
    if (prevState.totalPages !== totalPages || prevState.currentPage !== currentPage) {
      const pager = Pagination.getPager(total, length, currentPage);
      return {
        pages: pager.pages,
        totalPages,
        currentPage
      };
    }
    return null;
  }

  /**
   * Util to calculate number of anchors
   * @param totalItems - Total number of items
   * @param pageSize - Number of items per page
   * @param currentPage - Current active page
   * @returns Object containing array of page numbers and ellipses
   */
  static getPager(totalItems: number, pageSize: number, currentPage: number = APPCONSTANTS.NUMBER.ONE) {
    // Constants and initial setup
    const paginationRange = APPCONSTANTS.PAGINATION_RANGE;
    const pages: Array<number | string> = [];
    const totalPages = Math.max(Math.ceil(totalItems / pageSize), 1);

    // Ensure current page is within valid range
    currentPage = Math.max(1, Math.min(currentPage, totalPages));

    // Case 1: Total pages is less than or equal to pagination range - show all pages
    if (totalPages <= paginationRange) {
      return { pages: Array.from({ length: totalPages }, (_, i) => i + 1) };
    }

    // Case 2: First few pages (1-3)
    if (currentPage <= 3) {
      // Add first few pages
      const endPage = currentPage === 3 ? 4 : 3;
      for (let i = 1; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis and last page
      pages.push('...');
      pages.push(totalPages);
      return { pages };
    }

    // Case 3: Last few pages
    if (currentPage >= totalPages - 2) {
      pages.push('...');

      // Show 4 pages for third-to-last page, 3 pages otherwise
      const startPage = totalPages - (currentPage === totalPages - 2 ? 3 : 2);
      for (let i = startPage; i <= totalPages; i++) {
        pages.push(i);
      }
      return { pages };
    }

    // Case 4: Middle pages
    pages.push('...');
    pages.push(currentPage - 1);
    pages.push(currentPage);
    pages.push(currentPage + 1);
    pages.push('...');
    pages.push(totalPages);

    return { pages };
  }

  constructor(props: IPaginationProps) {
    super(props);
    this.state = {
      pages: [],
      totalPages: 0,
      currentPage: 1
    };
  }

  /**
   * Re-render components based on onChangePage number
   * @param length
   * @param total
   * @param page
   * @param reloadParent
   */
  setPage = (length: number, total: number, page: number, reloadParent: boolean) => {
    // get new pager object for specified onChangePage

    const pager = Pagination.getPager(total, length, page);
    if (page < 1 || page > Number(pager.pages[pager.pages.length - 1]) || page === this.state.currentPage) {
      return;
    }

    // update state
    this.setState({
      pages: pager.pages,
      currentPage: page
    });
    // call onChangePage function in parent component
    if (reloadParent) {
      this.props.onChangePage(page, length);
    }
  };

  /**
   * Render Anchor element
   * @param page
   * @param index
   * @returns {*}
   */
  renderAnchor = (page: any, index: number) => {
    const { currentPage } = this.state;
    if (page === '...') {
      return (
        <Anchor
          key={index}
          content={`${page}`}
          className={styles.cursorNotAllowed}
          onClick={() => {
            return;
          }}
        />
      );
    }
    return (
      <Anchor
        key={index}
        content={`${page}`}
        onClick={() => this.setPage(this.props.length, this.props.total, page, true)}
        className={currentPage === page ? styles.active : ''}
      />
    );
  };

  /**
   * Render Pagination Anchors
   * @returns {any[]}
   */
  renderAnchors = () => this.state.pages.map(this.renderAnchor);

  handleRowsPerPageChange = ({ value }: any) => {
    this.props.onChangeRowsPerPage(value);
  };

  render() {
    const { totalPages, currentPage } = this.state;
    const { length, total } = this.props;
    const isFirstPage = currentPage === 1;
    const isEndPage = currentPage === totalPages;
    const paginationLengthOptions = [
      { label: '10', value: '10' },
      { label: '20', value: '20' },
      { label: '50', value: '50' }
    ];
    const currentOption = paginationLengthOptions.find(({ value }) => Number(value) === length);
    return (
      <div className={styles.pagination}>
        <div className={`d-flex align-items-center ${styles.totalPage}`}>
          <span className={`pr-4 me-1 ${total?.toString()?.length >= 5 ? styles.customPagination : ''}`}>
            Rows per page
          </span>
          <Select
            menuPlacement='top'
            className='pagination-select'
            classNamePrefix='select-field'
            value={currentOption}
            options={paginationLengthOptions}
            onChange={this.handleRowsPerPageChange as any}
          />
        </div>
        <div className={styles.paginationWrapper}>
          <div className={`${styles.count} me-1 ${total?.toString()?.length >= 5 ? styles.customPagination : ''}`}>
            {` ${isFirstPage ? 1 : (currentPage - 1) * length + 1} - ${
              isEndPage ? total : currentPage * length
            } of ${total}`}
          </div>
          <div className='mx-1'>
            <PaginationInput
              onPagination={(page: number) => {
                this.setPage(length, total, page, true);
              }}
              maxNumber={Math.ceil(total / length)}
              type={'text'}
              placeholder='#page'
            />
          </div>
          <ul className='m-0 ps-1'>
            <li>
              <div
                className={`${styles.paginationButton} ${isFirstPage ? styles.disabled : ''}`}
                onClick={() => {
                  if (!isFirstPage) {
                    this.setPage(length, total, 1, true);
                  }
                }}
              >
                «
              </div>
            </li>
            <li>
              <div
                className={`${styles.paginationButton} ${isFirstPage ? styles.disabled : ''}`}
                onClick={() => {
                  if (!isFirstPage) {
                    this.setPage(length, total, currentPage - 1, true);
                  }
                }}
              >
                ‹
              </div>
            </li>
            {this.renderAnchors()}
            <li>
              <div
                className={`${styles.paginationButton} ${isEndPage ? styles.disabled : ''}`}
                onClick={() => {
                  if (!isEndPage) {
                    this.setPage(length, total, currentPage + 1, true);
                  }
                }}
              >
                ›
              </div>
            </li>
            <li>
              <div
                className={`${styles.paginationButton} ${isEndPage ? styles.disabled : ''}`}
                onClick={() => {
                  if (!isEndPage) {
                    this.setPage(length, total, Math.max(0, Math.ceil(total / length)), true);
                  }
                }}
              >
                »
              </div>
            </li>
          </ul>
        </div>
      </div>
    );
  }
}

export default Pagination;
